import axios from 'axios';
import { APP_BASE_URL, GET_KAVACH_EVENT_LOGS, GET_ASSETS_PAGINATED, GET_LATEST_TELEMETRY, GET_DEVICE_BY_CUSTOMER_ID, GET_TELEMETRY_HISTORY } from '../../../Common/EndPoints';
import {
  EventLogsApiResponse,
  GetEventLogsParams,
  TransformedEventLogsResponse,
  EventLogItem,
  NewTelemetryItem,
} from './kavachApiTypes';
import { transformEventLogsToKavachData, transformTelemetryToKavachData, transformEventLogToSwitch, aggregateSwitchData, generateAlertsFromSwitches, calculateStatsFromSwitches, calculateStationStats, transformTelemetryToSwitch } from './kavachDataTransform';
import { KavachDeviceAssetInfo } from './kavachTypes';

// History response interface
export interface KavachHistoryResponse {
  content: EventLogItem[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
}

export interface DeviceTypeResponse {
  error: DeviceTypeError
  success: boolean
  data: DeviceType[]
}

export interface DeviceTypeError {
  code: number
  message: string
  errors: any
}

export interface DeviceType {
  typeId: string
  type: string
  label: string
  devices: any
  collectionMaps: any
}


// Get auth token from localStorage
const getAuthToken = (): string => {
  try {
    // Try multiple token storage keys used in the app
    const token = localStorage.getItem('accessToken')
      || localStorage.getItem('token')
      || (window as any).jwtToken
      || '';
    return token;
  } catch {
    return '';
  }
};

// Create axios instance with default config
const createAxiosConfig = () => ({
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${getAuthToken()}`,
  },
});

/**
 * Parse device data into KavachDeviceAssetInfo
 * Device API returns:
 * - device.name: Full name like "NCR_PRYJ_MNR_MNR-DOWN-LINE"
 * - additionaldetail.name: Clean name like "MNR-DOWN-LINE" (matches event log name)
 */
const parseDeviceAsset = (device: any): KavachDeviceAssetInfo => {
  const additionaldetail = device.additionaldetail || {};
  const deviceType = device.deviceType || {};

  // Customer info can be in different formats
  const customerId = device.customer_id?.toString() || device.customerId?.toString() || device.customer?.id?.toString() || '';
  // Station name from additionaldetail (e.g., "MNR", "FZD", "HRS")
  // This is more reliable for display than customer name
  const customerName = additionaldetail.station || device.customer?.name || device.customerName || '';

  // Use additionaldetail.name (clean name like "MNR-DOWN-LINE") for matching with event logs
  // Fallback to device.name if additionaldetail.name is not available
  const cleanName = additionaldetail.name || device.name || '';

  return {
    deviceId: device.deviceid?.toString() || device.deviceId?.toString() || '',
    name: cleanName,
    deviceType: deviceType.label || additionaldetail.gearType || additionaldetail.device_type || additionaldetail.deviceType || 'Media Converter',
    // Make and Model - check multiple field name variations
    make: additionaldetail.make || additionaldetail.Make || additionaldetail.MAKE || '',
    model: additionaldetail.model || additionaldetail.Model || additionaldetail.MODEL || '',
    // Serial number - API uses "serial" field
    serialNumber: additionaldetail.serial || additionaldetail.serialNumber || additionaldetail.serial_number || device.clientassetid || '',
    manufactureNumber: additionaldetail.manufactureNumber || additionaldetail.manufacture_number || '',
    purchaseOrderNo: additionaldetail.purchaseOrderNo || additionaldetail.po_number || '',
    // Purchase date - API uses "purchase" field
    purchaseDate: additionaldetail.purchase || additionaldetail.purchaseDate || additionaldetail.purchase_date || '',
    // Installation date - API uses "install" field
    installationDate: additionaldetail.install || additionaldetail.installationDate || additionaldetail.installation_date || device.commissiondate || '',
    // Warranty expiry - API uses "warrenty" (misspelled) field
    warrantyExpiry: additionaldetail.warrenty || additionaldetail.warranty || additionaldetail.warrantyExpiry || additionaldetail.warranty_expiry || '',
    // Codal life - API uses "codal" field
    codalLife: additionaldetail.codal || additionaldetail.codalLife || additionaldetail.codal_life || '',
    firmwareVersion: additionaldetail.firmwareVersion || additionaldetail.firmware_version || '',
    macAddress: additionaldetail.macAddress || additionaldetail.mac_address || '',
    // Latitude/Longitude - API uses "lat" and "lon" fields
    latitude: additionaldetail.lat || additionaldetail.latitude || '',
    longitude: additionaldetail.lon || additionaldetail.lng || additionaldetail.longitude || '',
    assetPosition: additionaldetail.assetPosition || additionaldetail.location || '',
    // Platform - API uses "platform" field
    platformNo: additionaldetail.platform || additionaldetail.platformNo || '',
    installationTeam: additionaldetail.installationTeam || additionaldetail.installation_team || '',
    supervisor: additionaldetail.supervisor || '',
    customerId,
    customerName,
    isActive: device.isactive || false,
    additionaldetail: additionaldetail,
  };
};

/**
 * Fetch Kavach device assets from /v2/device API with pagination handling
 * This gets actual device info: name, type, make, model, serial number, etc.
 * Fetches all pages to ensure all devices are included
 * @param kavachSystemId - The system ID for KAVACH
 * @param customerIds - List of customer IDs to filter by
 * @returns Map of deviceId to device asset info
 */
/**
 * Normalize device name for matching
 * Extracts the core device identifier from different naming formats
 * Device API names like: "NCR13_PRYJ_FZD_FZD-UP-LINE", "NCR14_PRYJ_FZD_FZD-LSC-54-MNR-UP-LINE"
 * We use the FULL name as the key since event logs now have the same naming format
 */
const normalizeDeviceName = (name: string): string => {
  if (!name) return '';
  // Convert to uppercase and normalize for consistent matching
  return name.toUpperCase()
    .replace(/-ROUTER$/i, '')  // Remove -ROUTER suffix if present
    .trim();
};

export const fetchKavachDeviceAssets = async (
  kavachSystemId: string,
  customerIds: string[]
): Promise<Map<string, KavachDeviceAssetInfo>> => {
  // Map by normalized device name for matching with event logs
  const deviceMapByName = new Map<string, KavachDeviceAssetInfo>();
  const pageSize = 100; // Fetch 100 devices per page
  let currentPage = 1;
  let hasMorePages = true;


  try {
    while (hasMorePages) {
      const params = {
        customerIds: customerIds.toString(),
        softwareIds: kavachSystemId,
        with_type: true,
        is_active: true,
        pageSize: pageSize,
        page: currentPage,
      };

      const response = await axios.get(`${APP_BASE_URL}${GET_ASSETS_PAGINATED}`, {
        params,
        ...createAxiosConfig(),
      });

      const data = response?.data?.data;
      const devices = data?.content || [];
      const totalPages = data?.totalPages || 1;

      // Parse and add devices to map BY CLEAN NAME (additionaldetail.name)
      // This allows matching with event log's name field (e.g., "MXK-UP-LINE")
      devices.forEach((device: any) => {
        const deviceInfo = parseDeviceAsset(device);
        if (deviceInfo.name) {
          // Store by normalized clean name for matching with event logs
          const normalizedName = deviceInfo.name.toUpperCase().trim();
          deviceMapByName.set(normalizedName, deviceInfo);
        }
      });

      // Check if there are more pages
      currentPage++;
      hasMorePages = currentPage <= totalPages;

      // Safety limit to prevent infinite loops
      if (currentPage > 50) {
        console.warn('Reached maximum page limit (50) for device assets');
        break;
      }
    }
  } catch (error) {
    console.error('Error fetching Kavach device assets:', error);
  }

  return deviceMapByName;
};

/**
 * Get Kavach system ID from systems list in localStorage
 */
export const getKavachSystemId = (): string | null => {
  try {
    const systemsList = JSON.parse(localStorage.getItem('systems_list') || '[]');
    const kavachSystem = systemsList.find((sys: any) =>
      sys.code?.toUpperCase() === 'KAVACH' ||
      sys.name?.toUpperCase().includes('KAVACH')
    );
    return kavachSystem?.id || null;
  } catch {
    return null;
  }
};

/**
 * Get customer IDs from localStorage
 * Tries multiple keys used in the app
 */
export const getCustomerIds = (): string[] => {
  try {
    // Try different localStorage keys used in the app
    // 1. selected-customers (array of customer objects with id)
    const selectedCustomers = JSON.parse(localStorage.getItem('selected-customers') || '[]');
    if (selectedCustomers.length > 0) {
      const ids = selectedCustomers.map((c: any) => c.id?.toString() || c.customerId?.toString()).filter(Boolean);
      if (ids.length > 0) {
        return ids;
      }
    }

    // 2. customerIdList (comma-separated or JSON array)
    const customerIdList = localStorage.getItem('customerIdList');
    if (customerIdList) {
      try {
        const parsed = JSON.parse(customerIdList);
        const ids = Array.isArray(parsed) ? parsed.map(String) : [String(parsed)];
        if (ids.length > 0) {
          return ids;
        }
      } catch {
        // If not JSON, treat as comma-separated
        const ids = customerIdList.split(',').map(s => s.trim()).filter(Boolean);
        if (ids.length > 0) {
          return ids;
        }
      }
    }

    // 3. customerId (single customer ID)
    const customerId = localStorage.getItem('customerId');
    if (customerId) {
      return [customerId];
    }

    // 4. Original key (customerIds)
    const customerIds = JSON.parse(localStorage.getItem('customerIds') || '[]');
    if (customerIds.length > 0) {
      return customerIds;
    }

    return [];
  } catch {
    return [];
  }
};

export const getKavachDeviceType = async (systemId: string, customerIds: string[]) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('softwareIds', systemId);
    const url = `${APP_BASE_URL}${GET_DEVICE_BY_CUSTOMER_ID}?${queryParams.toString()}`;
    const response = await axios.post<DeviceTypeResponse>(url, customerIds, createAxiosConfig());
    return response.data.data[0].type || "Media Converter";
  } catch (error) {
    console.error('Error fetching Kavach device type:', error);
    return "Media Converter";
  }
}

/**
 * Fetch Kavach event logs for dashboard (latest status for all devices)
 * First fetches device assets from /v2/device API to get actual device info
 * Then fetches event logs and transforms using the device asset info
 * @param params - Query parameters (page, size)
 * @param kavachSystemId - Optional Kavach system ID (auto-detected if not provided)
 * @param customerIds - Optional customer IDs (auto-detected if not provided)
 * @returns Transformed dashboard data with switches, alerts, stats
 */
export const fetchKavachEventLogs = async (
  params: GetEventLogsParams = {},
  kavachSystemId?: string | null,
  customerIds?: string[]
): Promise<TransformedEventLogsResponse> => {
  try {
    // Step 1: Get Kavach system ID and customer IDs if not provided
    const systemId = kavachSystemId || getKavachSystemId();
    const custIds = params.stn ? [params.stn] : customerIds || getCustomerIds();

    // Step 2: Fetch device assets from /v2/device API (for device name, type, make, model, etc.)
    let deviceAssetMap = new Map<string, KavachDeviceAssetInfo>();
    if (systemId && custIds.length > 0) {
      deviceAssetMap = await fetchKavachDeviceAssets(systemId, custIds);
    }

    // Step 3: Fetch latest telemetry using new endpoint
    const deviceTypeCategory = await getKavachDeviceType(systemId || "", custIds);
    const paramBody = {
      softwareId: systemId,
      customerIds: custIds,
      status: "ALL",
      deviceCategory: deviceTypeCategory,
    };

    const url = `${APP_BASE_URL}${GET_LATEST_TELEMETRY}`;
    const response = await axios.post(url, paramBody, createAxiosConfig());

    // Check if we got valid data
    if (!response.data || !Array.isArray(response.data.data)) {
      console.warn('No telemetry data received from API');
      return {
        switches: [],
        alerts: [],
        stats: {
          totalSwitches: 0,
          portsUp: 0,
          portsDown: 0,
          activeAlerts: 0,
          upTrend: 0,
          downTrend: 0,
        },
        stationStats: [],
        stations: ['ALL'],
        totalElements: 0,
        totalPages: 0,
        currentPage: 0,
      };
    }
    const telemetryItems: NewTelemetryItem[] = response.data.data;

    // Step 4: Transform using device asset info (matched by normalized name)
    return transformTelemetryToKavachData(
      telemetryItems,
      telemetryItems.length,
      1, // Single page since we're getting all latest data
      0, // Current page
      deviceAssetMap
    );
  } catch (error) {
    console.error('Error fetching Kavach event logs:', error);
    throw error;
  }
};

/**
 * Fetch single device details by deviceId
 * Combines event log data with device asset info
 * NOTE: This still uses the old event logs endpoint for single device lookup
 * @param deviceName - The device Name to fetch
 * @returns KavachSwitch data or null if not found
 */
export const fetchKavachDeviceById = async (
  { deviceId, deviceName }: { deviceId: string, deviceName: string },
  kavachSystemId?: string | null,
  customerIds?: string[]
): Promise<{ switch: any; deviceAssetInfo: KavachDeviceAssetInfo | null } | null> => {
  try {
    // Step 1: Get Kavach system ID and customer IDs if not provided
    const systemId = kavachSystemId || getKavachSystemId();
    const custIds = customerIds || getCustomerIds();

    // Step 2: Fetch device assets from /v2/device API (for device name, type, make, model, etc.)
    let deviceAssetMap = new Map<string, KavachDeviceAssetInfo>();
    if (systemId && custIds.length > 0) {
      deviceAssetMap = await fetchKavachDeviceAssets(systemId, custIds);
    }

    // Step 3: Fetch latest telemetry using new endpoint
    const deviceTypeCategory = await getKavachDeviceType(systemId || "", custIds);
    const paramBody = {
      softwareId: systemId,
      customerIds: custIds,
      status: "ALL",
      deviceCategory: deviceTypeCategory,
      name: deviceName,
      page: 1,
      pageSize: 1,
    };

    const url = `${APP_BASE_URL}${GET_LATEST_TELEMETRY}`;
    const response = await axios.post(url, paramBody, createAxiosConfig());

    // Check if we got valid data
    if (!response.data || !Array.isArray(response.data.data) || response.data.data.length === 0) {
      console.warn('No telemetry data received from API');
      const assetInfo = deviceAssetMap.get(deviceId);
      if (assetInfo) {
        return { switch: null, deviceAssetInfo: assetInfo };
      }
    }

    const telemetryItems: NewTelemetryItem[] = response.data.data;

    // Step 4: Transform using device asset info (matched by normalized name)
    const switchData = transformTelemetryToSwitch(
      telemetryItems[0],
      deviceAssetMap
    );
    return {
      switch: switchData,
      deviceAssetInfo: deviceAssetMap.get(deviceId) || null
    };
  } catch (error) {
    console.error('Error fetching Kavach event logs:', error);
    throw error;
  }

  // try {
  //     // Get system ID and customer IDs
  //     const systemId = getKavachSystemId();
  //     const custIds = getCustomerIds();

  //     // Fetch device assets
  //     let deviceAssetMap = new Map<string, KavachDeviceAssetInfo>();
  //     if (systemId && custIds.length > 0) {
  //       deviceAssetMap = await fetchKavachDeviceAssets(systemId, custIds);
  //     }

  //     // Fetch event logs for this device using old endpoint
  //     const queryParams = new URLSearchParams();
  //     queryParams.append('page', '0');
  //     queryParams.append('size', '10');
  //     queryParams.append('nameLike', 'KAVACH');
  //     queryParams.append('deviceId', deviceId);

  //     const url = `${APP_BASE_URL}${GET_KAVACH_EVENT_LOGS}?${queryParams.toString()}`;
  //     const response = await axios.get<EventLogsApiResponse>(url, createAxiosConfig());

  //     if (!response.data.success || !response.data.data?.content?.length) {
  //       // If no event logs, at least return device asset info if available
  //       const assetInfo = deviceAssetMap.get(deviceId);
  //       if (assetInfo) {
  //         return { switch: null, deviceAssetInfo: assetInfo };
  //       }
  //       return null;
  //     }

  //     // Transform the event log to switch data using legacy functions
  //     const { content } = response.data.data;
  //     const deviceMap = aggregateSwitchData(content);

  //     const switches: any[] = [];
  //     deviceMap.forEach(({ eventLog, parsedData }) => {
  //       switches.push(transformEventLogToSwitch(eventLog, parsedData, deviceAssetMap));
  //     });

  //     if (switches.length > 0) {
  //       return {
  //         switch: switches[0],
  //         deviceAssetInfo: deviceAssetMap.get(deviceId) || null,
  //       };
  //     }

  //     return null;
  //   } catch (error) {
  //     console.error('Error fetching Kavach device by ID:', error);
  //     return null;
  //   }
};
// /**
//  * Fetch single device details by deviceId
//  * Combines event log data with device asset info
//  * NOTE: This still uses the old event logs endpoint for single device lookup
//  * @param deviceId - The device ID to fetch
//  * @returns KavachSwitch data or null if not found
//  */
// export const fetchKavachDeviceById = async (
//   deviceId: string
// ): Promise<{ switch: any; deviceAssetInfo: KavachDeviceAssetInfo | null } | null> => {
//   try {
//     // Get system ID and customer IDs
//     const systemId = getKavachSystemId();
//     const custIds = getCustomerIds();

//     // Fetch device assets
//     let deviceAssetMap = new Map<string, KavachDeviceAssetInfo>();
//     if (systemId && custIds.length > 0) {
//       deviceAssetMap = await fetchKavachDeviceAssets(systemId, custIds);
//     }

//     // Fetch event logs for this device using old endpoint
//     const queryParams = new URLSearchParams();
//     queryParams.append('page', '0');
//     queryParams.append('size', '10');
//     queryParams.append('nameLike', 'KAVACH');
//     queryParams.append('deviceId', deviceId);

//     const url = `${APP_BASE_URL}${GET_KAVACH_EVENT_LOGS}?${queryParams.toString()}`;
//     const response = await axios.get<EventLogsApiResponse>(url, createAxiosConfig());

//     if (!response.data.success || !response.data.data?.content?.length) {
//       // If no event logs, at least return device asset info if available
//       const assetInfo = deviceAssetMap.get(deviceId);
//       if (assetInfo) {
//         return { switch: null, deviceAssetInfo: assetInfo };
//       }
//       return null;
//     }

//     // Transform the event log to switch data using legacy functions
//     const { content } = response.data.data;
//     const deviceMap = aggregateSwitchData(content);

//     const switches: any[] = [];
//     deviceMap.forEach(({ eventLog, parsedData }) => {
//       switches.push(transformEventLogToSwitch(eventLog, parsedData, deviceAssetMap));
//     });

//     if (switches.length > 0) {
//       return {
//         switch: switches[0],
//         deviceAssetInfo: deviceAssetMap.get(deviceId) || null,
//       };
//     }

//     return null;
//   } catch (error) {
//     console.error('Error fetching Kavach device by ID:', error);
//     return null;
//   }
// };

/**
 * Fetch Kavach event logs history for a specific device with date range
 * @param params - Query parameters (page, size, deviceId, fromDate, toDate)
 * @returns History data with pagination info
 */

interface GetTelemetryHistoryParams {
  page?: number;
  size?: number;
  deviceId: string;      // Filter by specific device
  fromDate?: number;      // Start timestamp (epoch ms)
  toDate?: number;        // End timestamp (epoch ms)
}
interface KavachTelemetryHistoryApiResponse {
  error: {
    code: number
    message: string
    errors: any
  };
  success: boolean;
  data: {
    content: Array<{
      data: Array<{
        subGear: string
        dateTime: number
        values: {
          deviceIp: string
          totalResponseTimeMs: any
          monitoringType: string
          eventType: string
          deviceName: string
          results: string
          status: string
          PARENT_DEVICE: string
        }
        classification: any
      }>
    }>
    pageable: string
    totalElements: number
    totalPages: number
    last: boolean
    number: number
    sort: {
      sorted: boolean
      unsorted: boolean
      empty: boolean
    }
    size: number
    numberOfElements: number
    first: boolean
    empty: boolean
  }
}

export interface KavachTelemetryHistoryTransformedResponse {
  content: Array<{
    subGear: string
    dateTime: number

    deviceIp: string
    totalResponseTimeMs: any
    monitoringType: string
    eventType: string
    deviceName: string
    results: {
      [key: string]: {
        oid: string;
        value: string,
        responseTimeMs: number
      }
    }
    status: string
    PARENT_DEVICE: string
    classification: any
  }>,
  totalElements: number,
  totalPages: number,
  currentPage: number,
}

export const fetchKavachEventLogsHistory = async (
  params: GetTelemetryHistoryParams
): Promise<KavachTelemetryHistoryTransformedResponse> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params.page ?? 1).toString());
    queryParams.append('pageSize', (params.size ?? 100).toString());
    queryParams.append('deviceId', params.deviceId.toString());
    queryParams.append('withCount', 'true');
    queryParams.append('isModelView', 'true');
    queryParams.append('keys', 'deviceIp')


    if (params.fromDate !== undefined) {
      queryParams.append('startTs', params.fromDate.valueOf().toString());
    }
    if (params.toDate !== undefined) {
      queryParams.append('endTs', params.toDate.valueOf().toString());
    }

    const url = `${APP_BASE_URL}${GET_TELEMETRY_HISTORY}?${queryParams.toString()}`;
    const response = await axios.get<KavachTelemetryHistoryApiResponse>(url, createAxiosConfig());

    if (!response.data.success || !response.data.data) {
      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        currentPage: 0,
      };
    }

    const { content, totalElements, totalPages, number } = response.data.data;
    let _content = content.map(({ data, }) => {
      const { dateTime, values, classification, subGear, } = data[0];
      const { PARENT_DEVICE, deviceIp, deviceName, monitoringType, eventType, results, status, totalResponseTimeMs } = values;
      return {
        dateTime,
        classification,
        subGear,
        PARENT_DEVICE,
        deviceIp,
        deviceName,
        monitoringType,
        eventType,
        status,
        totalResponseTimeMs,
        results: JSON.parse(results)
      }
    });
    return {
      content: _content,
      totalElements,
      totalPages,
      currentPage: number,
    };
  } catch (error) {
    console.error('Error fetching Kavach event logs history:', error);
    throw error;
  }
};



// /**
//  * Fetch Kavach event logs history for a specific device with date range
//  * @param params - Query parameters (page, size, deviceId, fromDate, toDate)
//  * @returns History data with pagination info
//  */
// export const fetchKavachEventLogsHistory = async (
//   params: GetEventLogsParams
// ): Promise<KavachHistoryResponse> => {
//   try {
//     const queryParams = new URLSearchParams();
//     queryParams.append('page', (params.page ?? 0).toString());
//     queryParams.append('size', (params.size ?? 100).toString());
//     queryParams.append('nameLike', 'KAVACH');

//     if (params.deviceId !== undefined) {
//       queryParams.append('deviceId', params.deviceId.toString());
//     }
//     if (params.fromDate !== undefined) {
//       queryParams.append('fromDate', params.fromDate.toString());
//     }
//     if (params.toDate !== undefined) {
//       queryParams.append('toDate', params.toDate.toString());
//     }

//     const url = `${APP_BASE_URL}${GET_KAVACH_EVENT_LOGS}?${queryParams.toString()}`;
//     const response = await axios.get<EventLogsApiResponse>(url, createAxiosConfig());

//     if (!response.data.success || !response.data.data) {
//       return {
//         content: [],
//         totalElements: 0,
//         totalPages: 0,
//         currentPage: 0,
//       };
//     }

//     const { content, totalElements, totalPages, number } = response.data.data;
//     return {
//       content,
//       totalElements,
//       totalPages,
//       currentPage: number,
//     };
//   } catch (error) {
//     console.error('Error fetching Kavach event logs history:', error);
//     throw error;
//   }
// };
